import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import AddOutlined from '@mui/icons-material/AddOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import SummarizeOutlined from '@mui/icons-material/SummarizeOutlined';
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined';
import { qdfAPI } from '../../services/api';
import { AuthContext } from '../../contexts/AuthContext';
import AppShell from '../../components/layout/AppShell';
import { Button, PageHeader, PageLoader, EmptyState, Alert, ConfirmDialog } from '../../components/ui';
import SearchAndFilters from './components/SearchAndFilters';
import Pagination from './components/Pagination';
import QDFWorkOrderCard from './components/QDFWorkOrderCard';
import QDFWorkOrderDetailsModal from './components/QDFWorkOrderDetailsModal';
import QDFStatusCards from './components/QDFStatusCards';
import QDFFilterTabs from './components/QDFFilterTabs';
import MonthlySummaryModal from './components/MonthlySummaryModal';
import PendingPpmReportModal from './components/PendingPpmReportModal';
import { useQDFFiltering } from './hooks/useQDFFiltering';
import { useMonthlySummary } from './hooks/useMonthlySummary';
import { useBulkOperations } from './hooks/useBulkOperations';
import { generatePDF as generatePDFDocument } from './services/pdfService';
import { exportMonthlySummaryPdf } from './services/monthlySummaryPdf';
import { getCurrentMonthValue } from './utils/dateHelpers';

const QDFMainPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { role: userRole } = useContext(AuthContext);

  // Core state
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadingTooLong, setLoadingTooLong] = useState(false);
  const [hasRestored, setHasRestored] = useState(false);
  const [detailsEntry, setDetailsEntry] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [pendingPpmReportOpen, setPendingPpmReportOpen] = useState(false);

  // Custom hooks
  const {
    searchTerm, startDate, endDate, currentPage, activeTab, filteredEntries,
    paginatedEntries, totalPages, statusCounts, handleSearchChange,
    handleStartDateChange, handleEndDateChange, handlePageChange, setPageSilently,
    handleTabChange, clearFilters
  } = useQDFFiltering(entries, 5);

  const {
    selectedMonth, setSelectedMonth, monthlySummary, monthlySummaryError,
    monthlySummaryLoading, generatedReports, generatedReportsLoading,
    isMonthlySummaryModalOpen, setIsMonthlySummaryModalOpen,
    handleGenerateMonthlySummary, handleRefreshMonthlySummary, openMonthlySummaryModal,
    handleGenerateMonthlyPMs, pmGenerating, pmGenerationResult
  } = useMonthlySummary();

  // API & Handlers (defined before hooks)
  const fetchEntries = async () => {
    try {
      setLoading(true);
      const response = await qdfAPI.getReports({ limit: 10000 });
      const reports = response.data.reports || [];
      const sortedReports = reports.sort((a, b) => {
        const dateA = new Date(a.creationDate || a.createdAt);
        const dateB = new Date(b.creationDate || b.createdAt);
        return dateB - dateA;
      });
      setEntries(sortedReports);
      setError('');
    } catch (err) {
      console.error('Error fetching QDF reports:', err);
      setError(
        err.response?.status === 401 ? 'Authentication failed. Please log in again.' :
        err.response?.status === 404 ? 'QDF service is not available. Please try again later.' :
        'Failed to load QDF reports'
      );
    } finally {
      setLoading(false);
    }
  };

  const restoreState = useCallback(() => {
    const savedContext = sessionStorage.getItem('qdf-return-context');
    if (savedContext) {
      try {
        const context = JSON.parse(savedContext);
        if (context.type === 'edit') {
          if (context.activeTab && context.activeTab !== activeTab) {
            handleTabChange(context.activeTab, true);
          }
          if (context.searchTerm && context.searchTerm !== searchTerm) {
            handleSearchChange({ target: { value: context.searchTerm } }, true);
          }
          if (context.startDate && context.startDate !== startDate) {
            handleStartDateChange({ target: { value: context.startDate } }, true);
          }
          if (context.endDate && context.endDate !== endDate) {
            handleEndDateChange({ target: { value: context.endDate } }, true);
          }
          if (context.currentPage && context.currentPage !== currentPage) {
            setPageSilently(context.currentPage);
          }
          setTimeout(() => {
            window.scrollTo(0, context.scrollPosition || 0);
            sessionStorage.removeItem('qdf-return-context');
            setHasRestored(true);
          }, 0);
        } else {
          sessionStorage.removeItem('qdf-return-context');
          setHasRestored(true);
        }
      } catch (error) {
        console.error('Error restoring QDF context:', error);
        sessionStorage.removeItem('qdf-return-context');
        setHasRestored(true);
      }
    } else {
      setHasRestored(true);
    }
  }, [
    activeTab,
    currentPage,
    endDate,
    handleEndDateChange,
    handleSearchChange,
    handleStartDateChange,
    handleTabChange,
    searchTerm,
    setPageSilently,
    startDate
  ]);

  // The blocking window.confirm is replaced by a themed dialog; this only
  // records which entry is pending so the dialog can confirm it.
  const handleDelete = (id) => setPendingDeleteId(id);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await qdfAPI.deleteReport(pendingDeleteId);
      setEntries((prev) => prev.filter((e) => e._id !== pendingDeleteId));
      setDetailsEntry(null);
      toast.success('Work order deleted');
    } catch (err) {
      console.error('Error deleting QDF report:', err);
      toast.error('Failed to delete the report');
    } finally {
      setDeleting(false);
      setPendingDeleteId(null);
    }
  };

  const {
    selectedCards, isSelectionMode, isBulkDownloading, isConfirmingDelete, isBulkDeleting,
    toggleSelectionMode, handleCardSelect, handleSelectAll, handleBulkDelete,
    confirmBulkDelete, cancelBulkDelete, handleBulkDownload
  } = useBulkOperations(entries, fetchEntries);

  // Effects
  useEffect(() => {
    setSelectedMonth(getCurrentMonthValue());
  }, [setSelectedMonth]);

  useEffect(() => {
    fetchEntries();
  }, []);

  useEffect(() => {
    if (!loading && entries.length > 0 && !hasRestored) {
      restoreState();
    }
  }, [loading, entries, hasRestored, restoreState]);

  useEffect(() => {
    const saveScrollPosition = () => {
      if (!sessionStorage.getItem('qdf-return-context')) {
        sessionStorage.setItem('qdf-return-context', JSON.stringify({
          type: 'general',
          scrollPosition: window.scrollY,
          currentPage, activeTab, searchTerm, startDate, endDate
        }));
      }
    };

    const handleBeforeUnload = () => saveScrollPosition();
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      saveScrollPosition();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentPage, activeTab, searchTerm, startDate, endDate]);

  useEffect(() => {
    if (location.state?.refresh) {
      fetchEntries();
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    let timeoutId;
    if (loading) {
      timeoutId = setTimeout(() => setLoadingTooLong(true), 10000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [loading]);

  // Additional handlers
  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userId');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('userId');
    navigate('/login');
  };

  const handleCreate = () => {
    sessionStorage.setItem('qdf-return-context', JSON.stringify({
      type: 'add', scrollPosition: 0, currentPage: 1, activeTab, searchTerm, startDate, endDate
    }));
    navigate('/qdf-ppm-form');
  };

  const handleEdit = (entry) => {
    sessionStorage.setItem('qdf-return-context', JSON.stringify({
      type: 'edit', scrollPosition: window.scrollY, currentPage, activeTab, searchTerm, startDate, endDate
    }));
    navigate('/qdf-ppm-form', { state: { editReport: entry } });
  };

  const handleView = (entry) => {
    setDetailsEntry(entry);
  };

  const handleEntryUpdated = (updatedEntry) => {
    if (!updatedEntry?._id) return;

    setEntries((prevEntries) => prevEntries.map((entry) => (
      entry._id === updatedEntry._id ? { ...entry, ...updatedEntry } : entry
    )));
    setDetailsEntry(updatedEntry);
  };

  const handleViewPdf = (entry) => {
    const maintenanceType = entry.maintenanceType || (entry.ppmType ? 'preventive' : 'corrective');
    generatePDFDocument(entry, maintenanceType).catch((error) => {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    });
  };

  const handleEditFromDetails = (entry) => {
    setDetailsEntry(null);
    handleEdit(entry);
  };

  const handleExportMonthlySummaryPdf = () => {
    if (!monthlySummary) {
      toast.warn('No monthly summary data available to export.');
      return;
    }

    try {
      exportMonthlySummaryPdf(monthlySummary);
    } catch (error) {
      console.error('Monthly summary PDF export error:', error);
      toast.error('Failed to export monthly summary PDF');
    }
  };

  // Rendered once and shared by both the populated and empty states, so the
  // filters can never be missing from one of them.
  const searchAndFilters = (
    <SearchAndFilters
      searchTerm={searchTerm}
      startDate={startDate}
      endDate={endDate}
      totalResults={entries.length}
      filteredResults={filteredEntries.length}
      visibleCount={paginatedEntries.length}
      onSearchChange={handleSearchChange}
      onStartDateChange={handleStartDateChange}
      onEndDateChange={handleEndDateChange}
      onClearFilters={clearFilters}
      isSelectionMode={isSelectionMode}
      selectedCards={selectedCards}
      paginatedEntries={paginatedEntries}
      userRole={userRole}
      isBulkDownloading={isBulkDownloading}
      onToggleSelectionMode={toggleSelectionMode}
      onSelectAll={() => handleSelectAll(paginatedEntries)}
      onBulkDownload={handleBulkDownload}
      onBulkDelete={() => handleBulkDelete(paginatedEntries)}
    />
  );

  const emptyDescription = {
    all: 'No entries yet. Create one to get started.',
    'all-ppm': 'No preventive maintenance entries match these filters.',
    'ppm-active': 'No active preventive maintenance work.',
    'ppm-completed': 'No completed preventive maintenance work.',
    'all-sr': 'No service requests match these filters.',
    'sr-active': 'No active service requests.',
    'sr-completed': 'No completed service requests.',
  }[activeTab];

  return (
    <>
      <PageHeader
        sticky={false}
        title="QDF BGM System"
        subtitle="Work orders, preventive maintenance and service requests"
        actions={
          <>
            {userRole === 'admin' && (
              <Button variant="ghost" icon={TuneOutlined} onClick={() => navigate('/qdf-settings')}>
                Settings
              </Button>
            )}
            <Button variant="secondary" icon={SummarizeOutlined} onClick={openMonthlySummaryModal}>
              Monthly summary
            </Button>
            <Button variant="secondary" icon={AssignmentOutlined} onClick={() => setPendingPpmReportOpen(true)}>
              Pending PPM report
            </Button>
            <Button icon={AddOutlined} onClick={handleCreate}>
              Create
            </Button>
          </>
        }
      />

      <AppShell>
        {loading ? (
          <>
            <PageLoader
              label="Loading work orders"
              description="Fetching your maintenance data…"
            />
            {loadingTooLong && (
              <Alert
                variant="warning"
                title="Taking longer than usual"
                className="mx-auto max-w-md"
                action={
                  <Button variant="secondary" size="sm" onClick={handleLogout}>
                    Log out and retry
                  </Button>
                }
              >
                Your session may have expired.
              </Alert>
            )}
          </>
        ) : (
          <>
            <QDFStatusCards statusCounts={statusCounts} />

            {error && (
              <Alert variant="error" className="mb-6">
                {error}
              </Alert>
            )}

            <div className="flex flex-col gap-6 lg:flex-row">
              <QDFFilterTabs
                activeTab={activeTab}
                statusCounts={statusCounts}
                onChange={handleTabChange}
              />

              <div className="min-w-0 flex-1">
                {searchAndFilters}

                {filteredEntries.length === 0 ? (
                  <EmptyState
                    icon={AssignmentOutlined}
                    title="No work orders found"
                    description={emptyDescription}
                    action={
                      <Button icon={AddOutlined} onClick={handleCreate}>
                        Create a work order
                      </Button>
                    }
                  />
                ) : (
                  <>
                    {paginatedEntries.map((entry) => (
                      <QDFWorkOrderCard
                        key={entry._id}
                        entry={entry}
                        onView={handleView}
                        onEdit={handleEdit}
                        userRole={userRole}
                        isSelectionMode={isSelectionMode}
                        isSelected={selectedCards.has(entry._id)}
                        onSelect={handleCardSelect}
                      />
                    ))}
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={filteredEntries.length}
                      itemsPerPage={5}
                      onPageChange={handlePageChange}
                    />
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </AppShell>

      <MonthlySummaryModal isOpen={isMonthlySummaryModalOpen} onClose={() => setIsMonthlySummaryModalOpen(false)} selectedMonth={selectedMonth} onMonthChange={setSelectedMonth} monthlySummary={monthlySummary} monthlySummaryError={monthlySummaryError} monthlySummaryLoading={monthlySummaryLoading} generatedReports={generatedReports} generatedReportsLoading={generatedReportsLoading} onRefresh={handleRefreshMonthlySummary} onGenerate={handleGenerateMonthlySummary} userRole={userRole} onExportPdf={handleExportMonthlySummaryPdf} onGeneratePMs={async () => { await handleGenerateMonthlyPMs(); fetchEntries(); }} pmGenerating={pmGenerating} pmGenerationResult={pmGenerationResult} />

      {pendingPpmReportOpen && (
        <PendingPpmReportModal
          onClose={() => setPendingPpmReportOpen(false)}
          onView={(entry) => {
            setPendingPpmReportOpen(false);
            setDetailsEntry(entry);
          }}
        />
      )}

      <QDFWorkOrderDetailsModal
        isOpen={Boolean(detailsEntry)}
        entry={detailsEntry}
        onClose={() => setDetailsEntry(null)}
        onViewPdf={handleViewPdf}
        onEdit={handleEditFromDetails}
        onDelete={handleDelete}
        userRole={userRole}
        onInspectionSaved={handleEntryUpdated}
      />

      <ConfirmDialog
        isOpen={Boolean(pendingDeleteId)}
        onClose={() => setPendingDeleteId(null)}
        onConfirm={confirmDelete}
        loading={deleting}
        title="Delete work order"
        message="This permanently removes the work order and its inspection record. This cannot be undone."
        confirmLabel="Delete"
      />

      <ConfirmDialog
        isOpen={isConfirmingDelete}
        onClose={cancelBulkDelete}
        onConfirm={confirmBulkDelete}
        loading={isBulkDeleting}
        title={`Delete ${selectedCards.size} work ${selectedCards.size === 1 ? 'order' : 'orders'}`}
        message="This permanently removes the selected work orders and their inspection records. This cannot be undone."
        confirmLabel="Delete"
      />
    </>
  );
};

export default QDFMainPage;
